// Wire types and pure rules for the Polymarket US org accounts panel. Trader
// access is derived by the relay (migration 0042) from each member's role,
// org-key mode and the Polymarket US rule in Edit Limits, like Kalshi and
// Polymarket; the panel only reads it.

type UsGrantFlags = Record<'active' | 'viewOwn' | 'viewDesk' | 'buy' | 'sell' | 'cancelOwn' | 'cancelDesk', boolean>;

export interface UsOrgGrant extends UsGrantFlags {
  userId: string;
  username: string | null;
  /** False when the grantee is no longer a member of this org. */
  member: boolean;
}

export interface UsOrgAccount {
  id: string;
  protocol?: 'retail' | 'institutional';
  institutional?: {
    environment: 'preprod' | 'prod'; clientId: string; participantFirm: string;
    expectedIdentity: { user: string; firm: string; audience: string };
    participantId: string; userName: string; accountName: string;
    verification: { state: 'unverified' | 'verified'; checkedAt: string | null };
    runtimeAvailable: boolean;
  } | null;
  ownership: 'pooled' | 'personal';
  owner: { userId: string; username: string | null } | null;
  enabled: boolean;
  credential: { id: string; accessKeyLast4: string | null; revoked: boolean; createdAt: string | null } | null;
  grants: UsOrgGrant[];
  openingRecorded: boolean;
  /** The running relay's live worker state for this key (#987 step D), or
   * null when the relay did not report it. */
  runtime: {
    state: UsRuntimeState;
    /** Why a key is not connected, when the relay knows. */
    reason: string | null;
    /** The member the relay runs an enabled key as. */
    principalUserId: string | null;
  } | null;
}

export type UsRuntimeState = 'active' | 'connecting' | 'not_connected' | 'stopping' | 'disabled' | 'not_configured';

export interface UsOrgMember { id: string; username: string; role: string }

export interface UsOrgAccountsPayload {
  accounts: UsOrgAccount[];
  members: UsOrgMember[];
  runtimeAvailable: boolean;
  /** Configured transport only, not proof of usable grants/provider health.
   * Older payloads omit this, so verification remains unavailable. */
  institutionalVerificationAvailable?: boolean;
  /** Independent reference-only transport; no enrollment or trading authority. */
  institutionalReferenceAvailable?: boolean;
}

/** Read-only access summary for one account, current members only. Every
 * active row can sell and cancel its own orders; buy follows the member's
 * Polymarket US rule. Inactive rows are members the derivation leaves out:
 * personal-key mode on an org key, or a trader-role owner of a personal key. */
export interface UsAccessSummary {
  buyAndSell: string[];
  sellOnly: string[];
  desk: string[];
  noAccess: string[];
}

export function summarizeUsAccess(account: UsOrgAccount): UsAccessSummary {
  const summary: UsAccessSummary = { buyAndSell: [], sellOnly: [], desk: [], noAccess: [] };
  for (const g of account.grants) {
    if (!g.member) continue;
    const name = g.username ?? g.userId;
    if (!g.active) { summary.noAccess.push(name); continue; }
    if (g.buy) summary.buyAndSell.push(name);
    else if (g.sell) summary.sellOnly.push(name);
    if (g.viewDesk) summary.desk.push(name);
  }
  return summary;
}

export function hasLiveUsCredential(account: UsOrgAccount): boolean {
  return account.credential !== null && !account.credential.revoked;
}

export type UsActivationTone = 'ok' | 'pending' | 'warn' | 'muted';
export interface UsActivationLine { tone: UsActivationTone; text: string; detail?: string }
export interface UsActivationBadge { tone: UsActivationTone; label: string }

/** Live status, like the K/P credential state badges. A saved key is live as
 * soon as the relay's worker connects; no restart is involved. */
export const US_ACTIVATION = {
  active: 'Active',
  connecting: 'Connecting',
  notConnected: 'Not connected',
  stopping: 'Stopping',
  disabled: 'Disabled',
  notConfigured: 'Not configured',
  unavailable: 'Runtime status unavailable',
  opening: 'Waiting for the account\'s first venue read',
} as const;

const REASONS: Record<string, string> = {
  account_limit: 'This relay runs at most 32 Polymarket US keys at once. Disable another key to make room.',
  principal_access: 'The member this key runs as no longer has Polymarket US access. Disable and re-enable it to run it as you.',
  start_unavailable: 'The relay could not start this key and retries automatically.',
  connection_unavailable: 'The venue connection is failing; the relay keeps retrying.',
  market_data_unavailable: 'Polymarket US market data is unavailable on this relay; new orders wait for it.',
};

/** The key's status badge. Without runtime status it falls back to the saved
 * setting, which the activation list then qualifies as unconfirmed. */
export function usActivationBadge(account: UsOrgAccount): UsActivationBadge {
  if (account.protocol === 'institutional') return { tone: 'muted', label: account.credential?.revoked ? 'Revoked'
    : account.institutional?.verification.state === 'verified' ? 'Verified · trading unavailable'
      : hasLiveUsCredential(account) ? 'Not verified' : 'Setup required' };
  const runtime = account.runtime;
  if (!runtime) return { tone: 'muted', label: account.enabled ? 'Enabled' : US_ACTIVATION.disabled };
  switch (runtime.state) {
    case 'active': return { tone: 'ok', label: US_ACTIVATION.active };
    case 'connecting': return { tone: 'pending', label: US_ACTIVATION.connecting };
    case 'not_connected': return { tone: 'warn', label: US_ACTIVATION.notConnected };
    case 'stopping': return { tone: 'pending', label: US_ACTIVATION.stopping };
    case 'not_configured': return { tone: 'muted', label: US_ACTIVATION.notConfigured };
    default: return { tone: 'muted', label: US_ACTIVATION.disabled };
  }
}

/** Explanations under the badge. An active key with a recorded opening needs
 * none. Grants take effect per request, so they never make a key pending. */
export function usActivationLines(account: UsOrgAccount, memberName?: (id: string) => string | null): UsActivationLine[] {
  if (account.protocol === 'institutional') return [{ tone: 'muted', text: 'Institutional trading is not enabled.',
    detail: account.institutional?.verification.state === 'verified' ? 'Account identity verified; trading integration remains unavailable.'
      : 'Save the provisioned connection, then verify its account identity. Saving does not enable orders.' }];
  const lines: UsActivationLine[] = [];
  const runtime = account.runtime;
  if (!runtime) {
    lines.push({ tone: 'muted', text: US_ACTIVATION.unavailable,
      detail: 'This relay did not report whether the key is running.' });
  } else if (runtime.state === 'connecting') {
    lines.push({ tone: 'pending', text: US_ACTIVATION.connecting,
      detail: 'Trading starts on this key as soon as the connection is ready.' });
  } else if (runtime.state === 'not_connected') {
    lines.push({ tone: 'warn', text: US_ACTIVATION.notConnected,
      ...(runtime.reason && REASONS[runtime.reason] ? { detail: REASONS[runtime.reason] } : {}) });
  } else if (runtime.state === 'stopping') {
    lines.push({ tone: 'pending', text: US_ACTIVATION.stopping,
      detail: 'Disabled; the relay is closing this key\'s connection. No new orders.' });
  } else if (runtime.state === 'not_configured') {
    lines.push({ tone: 'muted', text: US_ACTIVATION.notConfigured,
      detail: 'This relay has no Polymarket US market data configured, so no key runs here.' });
  }
  if (account.enabled && !account.openingRecorded && runtime?.state !== 'not_configured') {
    lines.push({ tone: 'pending', text: US_ACTIVATION.opening,
      detail: 'Orders on this key start once it completes.' });
  }
  const principal = account.enabled ? runtime?.principalUserId ?? null : null;
  if (principal && memberName) {
    lines.push({ tone: 'muted', text: `Runs as ${memberName(principal) ?? 'a former member'}` });
  }
  return lines;
}

// ── Activity scan status (#1245) ────────────────────────────────

export type UsActivityScanReason = 'overlap' | 'eof' | 'failed' | 'stopped' | 'page_limit' | 'query_rejected'
  | 'conflict' | 'expired' | 'unavailable' | 'other' | 'none';

/** One account's worker evidence from GET /accounts/scan-status. */
export interface UsAccountScanStatus {
  accountId: string;
  activity: { reason: UsActivityScanReason; reasonCode: string | null; finishedAt: string | null };
  unresolvedGaps: number;
  invalidInboxFrames24h: number;
  unrecognizedActivity: { distinctItems24h: number; oldestObservedAt: string | null };
}

export interface UsScanStatusPayload {
  /** Server time of the read; ages are measured against it, not the browser clock. */
  readAt: string;
  windowHours: number;
  accounts: UsAccountScanStatus[];
}

const SCAN_WORDS: Record<UsActivityScanReason, string> = {
  overlap: 'caught up',
  eof: 'full history read',
  failed: 'failed',
  stopped: 'stopped',
  page_limit: 'paused at the page limit',
  query_rejected: 'refused by the venue',
  conflict: 'conflicting pages',
  expired: 'timed out',
  unavailable: 'could not run',
  other: 'ended',
  none: 'not run yet',
};
const SCAN_WARN = new Set<UsActivityScanReason>(['failed', 'query_rejected', 'conflict', 'unavailable']);
/** The worker scans every 30 s; a last chunk older than this means it stopped scanning. */
export const US_SCAN_STALE_MS = 5 * 60 * 1000;

function ago(fromIso: string | null, toIso: string): string | null {
  if (!fromIso) return null;
  const ms = Date.parse(toIso) - Date.parse(fromIso);
  if (!Number.isFinite(ms)) return null;
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** One compact status line: plain words in the text, exact reason codes and
 * times in the title. */
export function usScanStatusLine(status: UsAccountScanStatus, readAt: string): { tone: UsActivationTone; text: string; title: string } {
  const { activity, unresolvedGaps, invalidInboxFrames24h, unrecognizedActivity } = status;
  const age = ago(activity.finishedAt, readAt);
  const stale = activity.finishedAt !== null && Date.parse(readAt) - Date.parse(activity.finishedAt) > US_SCAN_STALE_MS;
  const parts = [`Activity scan ${SCAN_WORDS[activity.reason]}${age ? ` ${age} ago` : ''}`,
    plural(unresolvedGaps, 'execution gap', 'execution gaps'),
    `${plural(invalidInboxFrames24h, 'rejected frame', 'rejected frames')} (24h)`];
  if (unrecognizedActivity.distinctItems24h > 0) {
    const since = ago(unrecognizedActivity.oldestObservedAt, readAt);
    parts.push(`${plural(unrecognizedActivity.distinctItems24h, 'unknown activity', 'unknown activities')}${since ? ` since ${since}` : ''}`);
  }
  const warn = SCAN_WARN.has(activity.reason) || unresolvedGaps > 0 || invalidInboxFrames24h > 0
    || unrecognizedActivity.distinctItems24h > 0;
  const healthy = activity.reason === 'overlap' || activity.reason === 'eof';
  const tone: UsActivationTone = warn ? 'warn' : stale || !healthy ? 'pending' : 'ok';
  const title = [
    `Last activity chunk: ${activity.reasonCode ?? 'none'}${activity.finishedAt ? `, ended ${activity.finishedAt}` : ''}`,
    `Unresolved execution gaps: ${unresolvedGaps}`,
    `Rejected private-inbox frames (last 24 h): ${invalidInboxFrames24h}`,
    `Unrecognized activity items (last 24 h): ${unrecognizedActivity.distinctItems24h}`
      + (unrecognizedActivity.oldestObservedAt ? `, first saved ${unrecognizedActivity.oldestObservedAt}` : ''),
    `Read ${readAt}`,
  ].join('\n');
  return { tone, text: parts.join(' · '), title };
}
